import React from "react";
import { BadgeCheck } from "lucide-react";

/**
 * Verified Advocate badge — the single source of truth for the
 * Advoset "Bar Council verified" trust signal. Reused on:
 *   - Lawyer cards in search results (`components/LawyerCard.jsx`)
 *   - Lawyer detail page (`pages/LawyerDetail.jsx`)
 *   - Booking / consultation pages (`pages/Booking.jsx`)
 *
 * Truthy signals (any one is enough because the platform surfaces the
 * badge as soon as any explicit BCI approval exists):
 *   - lawyer.verified_advocate === true    (new API contract)
 *   - lawyer.bar_council_verified === true (raw lawyers doc field)
 *   - lawyer.bar_council_status === "verified"
 *   - lawyer.verified === true             (legacy admin-verify path)
 *
 * If none of the above are true the component renders `null` — never
 * a hollow placeholder — so unverified lawyers get no visual credit.
 */
export const isVerifiedAdvocate = (lawyer) => Boolean(
  lawyer && (
    lawyer.verified_advocate === true
    || lawyer.bar_council_verified === true
    || (lawyer.bar_council_status || "").toLowerCase() === "verified"
    || lawyer.verified === true
  )
);

export default function VerifiedAdvocateBadge({
  lawyer, size = "sm", showLabel = false, className = "",
}) {
  if (!isVerifiedAdvocate(lawyer)) return null;

  const iconPx = size === "lg" ? 18 : size === "md" ? 15 : 13;
  const boxCls = {
    lg: "text-sm px-2.5 py-1 gap-1.5",
    md: "text-xs px-2 py-0.5 gap-1",
    sm: "text-[11px] px-1.5 py-0.5 gap-1",
  }[size] || "text-xs px-2 py-0.5 gap-1";

  const label = showLabel ? "Verified Advocate" : null;

  return (
    <span
      className={
        "inline-flex items-center rounded-full font-bold uppercase tracking-wider "
        + "bg-[#0A2540] text-[#D4AF37] " + boxCls + " " + className
      }
      title="Bar Council verified by Advoset"
      data-testid={`verified-advocate-badge-${lawyer.id || lawyer.lawyer_id || ""}`}
      aria-label="Verified Advocate — Bar Council verified by Advoset"
    >
      <BadgeCheck size={iconPx} strokeWidth={2.5} />
      {label}
    </span>
  );
}
